import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "../components/Reveal";
import Seo from "../components/Seo";
import { fetchArticles, fetchSettings } from "../lib/api";
import { fmtLongDate, showsReadTime } from "../lib/dates";

export default function StudioLife() {
    const [articles, setArticles] = useState([]);
    const [categories, setCategories] = useState([]);
    const [activeCat, setActiveCat] = useState("All");

    useEffect(() => {
        fetchArticles().then(setArticles).catch(() => {});
        fetchSettings()
            .then((s) => setCategories(s?.article_categories || []))
            .catch(() => {});
    }, []);

    const visibleCategories = useMemo(() => {
        const used = new Set(
            articles.map((a) => (a.category || "").toUpperCase()).filter(Boolean)
        );
        return ["All", ...categories.filter((c) => used.has(c.toUpperCase()))];
    }, [articles, categories]);

    const filtered = useMemo(() => {
        if (activeCat === "All") return articles;
        return articles.filter(
            (a) => (a.category || "").toUpperCase() === activeCat.toUpperCase()
        );
    }, [articles, activeCat]);

    return (
        <main data-testid="page-studio-life" className="pt-32 md:pt-40">
            <Seo
                path="/studio-life"
                title="Studio Life — Notes from ASC Interior"
                description="People, projects, press, and perspectives from the studio."
            />
            <section className="px-6 md:px-12 lg:px-24 pb-20">
                <Reveal>
                    <p className="asc-overline">Studio Life</p>
                </Reveal>
                <Reveal delay={100}>
                    <h1 className="serif text-5xl sm:text-6xl lg:text-7xl mt-6 leading-[1.02] tracking-tight font-light max-w-4xl">
                        Notes from <span className="italic">the studio.</span>
                    </h1>
                </Reveal>
                <Reveal delay={180}>
                    <p className="mt-10 max-w-2xl text-lg text-[#5C5855] leading-relaxed">
                        People, projects, press, and perspectives from the studio.
                    </p>
                </Reveal>
            </section>

            <section className="px-6 md:px-12 lg:px-24 pb-8">
                {visibleCategories.length > 1 && (
                    <div
                        data-testid="studio-life-filters"
                        className="border-t border-[#E5E1DB] pt-8 flex flex-wrap gap-x-6 gap-y-3"
                    >
                        {visibleCategories.map((c) => (
                            <button
                                key={c}
                                onClick={() => setActiveCat(c)}
                                data-testid={`sl-filter-${c.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                                className={`pb-1 text-[11px] uppercase tracking-[0.22em] transition-colors ${
                                    activeCat === c
                                        ? "text-[#1A1A1A] border-b border-[#1A1A1A]"
                                        : "text-[#8C7A6B] hover:text-[#1A1A1A]"
                                }`}
                            >
                                {c}
                            </button>
                        ))}
                    </div>
                )}
            </section>

            <section className="px-6 md:px-12 lg:px-24 pb-32">
                {filtered.length === 0 ? (
                    <p className="text-[#5C5855]" data-testid="studio-life-empty">
                        No entries in this category yet.
                    </p>
                ) : (
                    <div
                        data-testid="studio-life-list"
                        className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-14"
                    >
                        {filtered.map((a, i) => {
                            const showRead = showsReadTime(a.category);
                            return (
                                <Reveal key={a.slug} delay={i * 80}>
                                    <Link
                                        to={`/studio-life/${a.slug}`}
                                        data-testid={`studio-life-card-${a.slug}`}
                                        className="block group"
                                    >
                                        <div className="asc-img-zoom aspect-[4/5] mb-5">
                                            {a.featured_image && (
                                                <img
                                                    src={a.featured_image}
                                                    alt={a.title}
                                                    className="w-full h-full object-cover"
                                                />
                                            )}
                                        </div>
                                        <p className="asc-overline">
                                            {a.category}
                                            {showRead && a.read_time ? ` · ${a.read_time}` : ""}
                                        </p>
                                        <h2 className="serif text-3xl mt-3 tracking-tight leading-snug">
                                            {a.title}
                                        </h2>
                                        {a.deck && (
                                            <p className="mt-4 text-[#5C5855] leading-relaxed">
                                                {a.deck}
                                            </p>
                                        )}
                                        <div className="flex items-center justify-between mt-6 text-[11px] uppercase tracking-[0.22em] text-[#8C7A6B]">
                                            <span>{fmtLongDate(a.publish_date)}</span>
                                            <ArrowUpRight
                                                size={16}
                                                className="transition-transform group-hover:-translate-y-1 group-hover:translate-x-1"
                                            />
                                        </div>
                                    </Link>
                                </Reveal>
                            );
                        })}
                    </div>
                )}
            </section>
        </main>
    );
}
